import { useAuthStore } from '@sunrise-ui/primitives';
import {
  useFavoriteSession,
  useGetUserSessionFavoriteIds,
} from '../queries/sessions';

/**
 * Hook to auto-favorite a session if not already favorited.
 * Returns a function that checks if the session is already favorited,
 * and if not, favorites it.
 *
 * @param sessionId - The session ID to favorite
 * @param eventId - The event ID for cache management
 * @param communityId - The community ID for cache management
 * @returns Object with autoFavorite function and loading state
 */
export function useAutoFavoriteSession(
  sessionId: string | undefined,
  eventId: string | undefined,
  communityId: string | undefined,
) {
  const { isAuthenticated } = useAuthStore();

  // Fetch user session favorites to check if session is already favorited
  const { data: userSessionFavorites } = useGetUserSessionFavoriteIds({
    eventId,
    communityId,
  });

  const { mutate: favoriteSession, isPending } = useFavoriteSession(
    sessionId ?? '',
    eventId,
  );

  /**
   * Auto-favorite the session if not already favorited
   */
  const autoFavorite = () => {
    if (!sessionId || !isAuthenticated) {
      return;
    }

    const isAlreadyFavorited = userSessionFavorites?.some(
      (id) => id === sessionId,
    );

    // Only favorite if not already favorited
    if (!isAlreadyFavorited) {
      favoriteSession();
    }
  };

  return {
    autoFavorite,
    isPending,
  };
}
